import { useTranslation } from "react-i18next";
import {
  Compass,
  Coffee,
  Sparkles,
  Banknote,
  HeartPulse,
  Zap,
  Brain,
  Users,
  Send,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { AppTabs, AppButton, AppInput } from "@/components/ui";
import { getScrollBehavior } from "@/hooks/use-scroll-spy";

const TAB_CONFIG = [
  { id: "executive", icon: Compass, activeColor: "data-[selected]:bg-success" },
  { id: "team", icon: Coffee, activeColor: "data-[selected]:bg-accent" },
  { id: "ai", icon: Sparkles, activeColor: "data-[selected]:bg-rose" },
  { id: "financial", icon: Banknote, activeColor: "data-[selected]:bg-warning" },
] as const;

/**
 * Platform preview section — tabbed showcase of Neural capabilities.
 * Uses HeroUI Tabs with className overrides for luxury aesthetic.
 * SC-008: Tab switch response within 200ms.
 */
export function PlatformPreview() {
  const { t } = useTranslation();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: getScrollBehavior() });
    }
  };

  return (
    <section id="platform" className="py-32 px-6 bg-background scroll-mt-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-sm font-bold text-success uppercase tracking-[0.4em]">
            {t("landing.platform.subtitle")}
          </h2>
          <h3 className="text-4xl md:text-5xl font-bold">
            {t("landing.platform.heading")}
          </h3>
        </div>

        <AppTabs
          defaultSelectedKey="executive"
          className="bg-background rounded-3xl border border-border shadow-2xl overflow-hidden ring-1 ring-success/5 [&_.tabs__indicator]:hidden"
        >
          {/* Tab bar */}
          <AppTabs.ListContainer className="flex flex-col lg:flex-row border-b border-border bg-surface/80 backdrop-blur-xl rounded-t-3xl">
            <AppTabs.List className="gap-1.5 px-3 py-4 lg:px-5 lg:py-4 lg:border-r border-border bg-transparent rounded-none items-center" style={{ borderRadius: 0 }}>
              {TAB_CONFIG.map((tab) => {
                const Icon = tab.icon;
                const tabData = t(`landing.platform.tabs.${tab.id}`, {
                  returnObjects: true,
                }) as { label: string };
                return (
                  <AppTabs.Tab
                    key={tab.id}
                    id={tab.id}
                    className={`w-auto h-auto px-5 py-2.5 rounded-full text-xs font-bold transition-all gap-2 cursor-pointer text-foreground-muted hover:text-foreground bg-transparent ${tab.activeColor} data-[selected]:text-background`}
                  >
                    <Icon className="w-4 h-4" /> {tabData.label}
                  </AppTabs.Tab>
                );
              })}
            </AppTabs.List>
            <div className="shrink-0 px-6 py-3 flex flex-col items-end justify-center text-foreground-muted text-[10px] font-mono tracking-widest uppercase hidden lg:flex gap-1">
              {t("landing.platform.statusLine").split(" // ").filter(Boolean).map((line, i) => (
                <span key={i}>// {line.replace(/^\/\/\s*/, "")}</span>
              ))}
            </div>
          </AppTabs.ListContainer>

          {/* Tab panels */}
          <AppTabs.Panel id="executive" className="p-8 md:p-16 mt-0 min-h-[600px] flex items-center">
            <ExecutiveTab scrollToSection={scrollToSection} />
          </AppTabs.Panel>
          <AppTabs.Panel id="team" className="p-8 md:p-16 mt-0 min-h-[600px] flex items-center">
            <TeamTab />
          </AppTabs.Panel>
          <AppTabs.Panel id="ai" className="p-8 md:p-16 mt-0 min-h-[600px] flex items-center">
            <AiTab />
          </AppTabs.Panel>
          <AppTabs.Panel id="financial" className="p-8 md:p-16 mt-0 min-h-[600px] flex items-center">
            <FinancialTab />
          </AppTabs.Panel>
        </AppTabs>
      </div>
    </section>
  );
}

function ExecutiveTab({ scrollToSection }: { scrollToSection: (id: string) => void }) {
  const { t } = useTranslation();
  const tabData = t("landing.platform.tabs.executive", {
    returnObjects: true,
  }) as {
    title: string;
    description: string;
    metrics: { label: string; value: string; color: string }[];
    progressBars: { label: string; value: number }[];
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 w-full">
      <div className="space-y-12">
        <div className="space-y-4">
          <h4 className="text-3xl font-bold">{tabData.title}</h4>
          <p className="text-lg text-foreground-secondary font-light">
            {tabData.description}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-6">
          {tabData.metrics.map((metric, i) => (
            <div
              key={i}
              className="bg-surface p-6 rounded-3xl border border-border"
            >
              <p
                className={`text-3xl font-bold ${metric.color === "pine" ? "text-success" : "text-rose"}`}
              >
                {metric.value}
              </p>
              <p className="text-[10px] uppercase font-bold text-foreground-muted tracking-widest mt-1">
                {metric.label}
              </p>
            </div>
          ))}
        </div>
        <AppButton
          variant="ghost"
          onPress={() => scrollToSection("how-it-works")}
          className="flex items-center gap-2 h-auto min-w-0 p-0 bg-transparent hover:bg-transparent data-[hovered]:bg-transparent text-success font-bold text-sm hover:translate-x-2 transition-all group"
        >
          View R&D Model{" "}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </AppButton>
      </div>
      <div className="bg-surface rounded-[3rem] border border-border p-10 flex flex-col justify-center shadow-inner">
        <div className="space-y-8">
          {tabData.progressBars.map((bar, i) => (
            <div key={i} className="space-y-2">
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-tighter">
                <span>{bar.label}</span>
                <span>{bar.value}%</span>
              </div>
              <div className="h-2 w-full bg-surface-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-success rounded-full"
                  style={{ width: `${bar.value}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TeamTab() {
  const { t } = useTranslation();
  const tabData = t("landing.platform.tabs.team", {
    returnObjects: true,
  }) as {
    title: string;
    description: string;
    wellnessAlert: string;
    features: { title: string; subtitle: string }[];
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 w-full">
      <div className="bg-surface rounded-[3rem] border border-border p-10 shadow-inner relative overflow-hidden flex flex-col justify-center">
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-background">
                <HeartPulse className="w-5 h-5" />
              </div>
              <span className="text-sm font-bold uppercase tracking-widest">
                Wellness Index
              </span>
            </div>
            <span className="text-xs font-mono text-rose">
              High Risk Alert
            </span>
          </div>
          <div className="p-4 bg-background rounded-2xl border border-rose/20">
            <p className="text-xs italic">&quot;{tabData.wellnessAlert}&quot;</p>
          </div>
        </div>
      </div>
      <div className="space-y-12">
        <div className="space-y-4">
          <h4 className="text-3xl font-bold">{tabData.title}</h4>
          <p className="text-lg text-foreground-secondary font-light">
            {tabData.description}
          </p>
        </div>
        <div className="space-y-6">
          {tabData.features.map((feature, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-10 h-10 bg-background rounded-xl flex items-center justify-center border border-border">
                {i === 0 ? (
                  <Zap className="w-5 h-5 text-accent" />
                ) : (
                  <Coffee className="w-5 h-5 text-accent" />
                )}
              </div>
              <div>
                <p className="text-sm font-bold">{feature.title}</p>
                <p className="text-[10px] text-foreground-muted">
                  {feature.subtitle}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AiTab() {
  const { t } = useTranslation();
  const tabData = t("landing.platform.tabs.ai", {
    returnObjects: true,
  }) as {
    query: string;
    response: string;
    placeholder: string;
  };

  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="bg-surface border border-border rounded-[3rem] p-8 shadow-inner">
        <div className="space-y-6 h-[350px] overflow-y-auto custom-scrollbar pr-4 mb-6">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-xl bg-rose flex-shrink-0 flex items-center justify-center text-background">
              <Brain className="w-6 h-6" />
            </div>
            <div className="bg-background p-5 rounded-2xl rounded-tl-none border border-border text-sm leading-relaxed max-w-[85%]">
              {tabData.query}
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <div className="bg-success/10 p-5 rounded-2xl rounded-tr-none border border-success/20 text-sm leading-relaxed max-w-[85%]">
              {tabData.response}
            </div>
            <div className="w-10 h-10 rounded-full bg-surface-secondary flex-shrink-0 flex items-center justify-center text-foreground-muted">
              <Users className="w-5 h-5" />
            </div>
          </div>
        </div>
        <div className="relative group">
          <AppInput
            disabled
            type="text"
            placeholder={tabData.placeholder}
            className="w-full bg-background border border-border rounded-2xl py-5 pl-8 pr-16 text-sm placeholder-foreground-muted shadow-none"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 p-3 bg-rose rounded-xl text-background opacity-50">
            <Send className="w-4 h-4" />
          </div>
        </div>
      </div>
    </div>
  );
}

function FinancialTab() {
  const { t } = useTranslation();
  const tabData = t("landing.platform.tabs.financial", {
    returnObjects: true,
  }) as {
    title: string;
    description: string;
    features: string[];
    totalInvestment: string;
    totalLabel: string;
    capex: string;
    opex: string;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 w-full">
      <div className="space-y-12 flex flex-col justify-center">
        <div className="space-y-4">
          <h4 className="text-3xl font-bold">{tabData.title}</h4>
          <p className="text-lg text-foreground-secondary font-light">
            {tabData.description}
          </p>
        </div>
        <ul className="space-y-4">
          {tabData.features.map((feature, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <ShieldCheck className="w-5 h-5 text-warning" /> {feature}
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-surface rounded-[3rem] border border-border p-10 flex flex-col items-center justify-center shadow-inner">
        <div className="w-full space-y-6">
          <div className="bg-background p-6 rounded-2xl border border-border text-center">
            <p className="text-[10px] text-foreground-muted uppercase font-bold tracking-widest mb-1">
              {tabData.totalLabel}
            </p>
            <p className="text-4xl font-bold text-warning">
              {tabData.totalInvestment}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-background p-4 rounded-xl border border-border text-center">
              <p className="text-[10px] text-foreground-muted uppercase font-bold">
                CapEx
              </p>
              <p className="text-lg font-bold">{tabData.capex}</p>
            </div>
            <div className="bg-background p-4 rounded-xl border border-border text-center">
              <p className="text-[10px] text-foreground-muted uppercase font-bold">
                OpEx
              </p>
              <p className="text-lg font-bold">{tabData.opex}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
